// src/components/admin/sections/AdminHero.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo from '../../../service/api_elcristo';

const AdminHero: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    primaryButtonText: '',
    primaryButtonLink: '',
    secondaryButtonText: '',
    secondaryButtonLink: '',
    heroImage: '',
    heroBackgroundImage: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getHeroData();
      setFormData(data);
    } catch (error) {
      console.error('Error loading hero data:', error);
      setMessage({ type: 'error', text: 'Error al cargar los datos' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await apiElCristo.updateHeroData(formData);
      setMessage({ type: 'success', text: '✅ Hero actualizado correctamente' });
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving hero data:', error);
      setMessage({ type: 'error', text: '❌ Error al guardar: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      <div className="content-header">
        <h1><i className="fas fa-home"></i> Editar Hero Section</h1>
        <p>Página principal - Primera sección visible</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-section">
          <h3><i className="fas fa-heading"></i> Textos Principales</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="subtitle">Subtítulo</label>
              <input
                type="text"
                id="subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                placeholder="Ej: Bienvenidos"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="title">
                Título Principal
                <span className="label-hint">Usa &lt;span&gt; para resaltar palabras</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Ej: Año escolar <span>2025</span>"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Descripción</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Descripción breve del año escolar"
              rows={3}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3><i className="fas fa-mouse-pointer"></i> Botones de Acción</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="primaryButtonText">Texto Botón Principal</label>
              <input
                type="text"
                id="primaryButtonText"
                name="primaryButtonText"
                value={formData.primaryButtonText}
                onChange={handleChange}
                placeholder="Ej: Conócenos"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="primaryButtonLink">Link Botón Principal</label>
              <input
                type="text"
                id="primaryButtonLink"
                name="primaryButtonLink"
                value={formData.primaryButtonLink}
                onChange={handleChange}
                placeholder="Ej: #nosotros"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="secondaryButtonText">Texto Botón Secundario</label>
              <input
                type="text"
                id="secondaryButtonText"
                name="secondaryButtonText"
                value={formData.secondaryButtonText}
                onChange={handleChange}
                placeholder="Ej: Radio El Cristo"
              />
            </div>

            <div className="form-group">
              <label htmlFor="secondaryButtonLink">Link Botón Secundario</label>
              <input
                type="text"
                id="secondaryButtonLink"
                name="secondaryButtonLink"
                value={formData.secondaryButtonLink}
                onChange={handleChange}
                placeholder="Ej: ./radio"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3><i className="fas fa-image"></i> Imágenes</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="heroImage">
                Imagen Hero
                <span className="label-hint">URL de la imagen principal</span>
              </label>
              <input
                type="text"
                id="heroImage"
                name="heroImage"
                value={formData.heroImage}
                onChange={handleChange}
                placeholder="/assets/kidsa/assets/img/hero/hero-shape.png"
              />
            </div>

            <div className="form-group">
              <label htmlFor="heroBackgroundImage">
                Imagen de Fondo
                <span className="label-hint">URL de la imagen de fondo</span>
              </label>
              <input
                type="text"
                id="heroBackgroundImage"
                name="heroBackgroundImage"
                value={formData.heroBackgroundImage}
                onChange={handleChange}
                placeholder="/assets/kidsa/assets/img/hero/bottom.png"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={loadData}
            disabled={saving}
          >
            <i className="fas fa-undo"></i>
            Recargar
          </button>
          
          <button 
            type="submit" 
            className="btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Preview */}
      <div className="preview-section">
        <h3><i className="fas fa-eye"></i> Vista Previa</h3>
        <div className="preview-box">
          <div className="hero-preview">
            <p className="preview-subtitle">{formData.subtitle}</p>
            <h1 
              className="preview-title" 
              dangerouslySetInnerHTML={{ __html: formData.title }}
            />
            <p className="preview-description">{formData.description}</p>
            <div className="preview-buttons">
              <button className="btn-preview-primary">
                {formData.primaryButtonText}
              </button>
              {formData.secondaryButtonText && (
                <button className="btn-preview-secondary">
                  {formData.secondaryButtonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHero;